import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type {
  ApiError,
  LoginData,
  User,
} from '../../types';

import {
  login,
  register,
  logout,
  refreshToken,
} from '../actions/authActions';

interface AuthState {
  data: LoginData | null;
  user: User | null;
  token?: string;                
  isAuthenticated: boolean;
  isInitialized: boolean;
  loading: boolean;
  error: ApiError | null;
}

const initialState: AuthState = {
  data: null,
  user: null,
  token: undefined,
  isAuthenticated: false,
  isInitialized: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    restoreSession: (state, action: PayloadAction<LoginData>) => {
      state.data = action.payload;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      state.isInitialized = true;
      state.loading = false;
      state.error = null;
      state.user = {
        id: action.payload.userId,
        email: action.payload.email,
      };
    },
    finishAuthInitialization: (state) => {
      state.isInitialized = true;
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
   
    updateProfile: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
          email: action.payload.email ?? state.user.email, 
        };
      }
    },

    clearError: (state) => {
      if (state.error === null) {
        return;
      }

      state.error = null;
    },

    resetAuth: () => ({ ...initialState, isInitialized: true }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.token = action.payload.token; 
        state.isAuthenticated = true;
        state.isInitialized = true;

        state.user = {
          id: action.payload.userId,
          email: action.payload.email,
        };
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          message: 'Login failed',
          code: 'LOGIN_FAILED',
        };
      })

      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = {
          ...action.payload,
          email: action.payload.email,
        };
        state.isAuthenticated = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? {
          message: 'Registration failed',
          code: 'REGISTRATION_FAILED',
        };
      })

      .addCase(logout.fulfilled, () => ({ ...initialState, isInitialized: true }))

      .addCase(refreshToken.fulfilled, (state, action) => {
        if (action.payload) {
          state.token = action.payload;
        }
      });
  },
});

export const {
  restoreSession,
  finishAuthInitialization,
  setAuthenticated,   
  updateProfile,
  clearError,
  resetAuth,
} = authSlice.actions;

export default authSlice.reducer;
